import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import ApiService from 'redux/services/api-service';
import {CampaignTestControlSelection, ListResult, SingleResult} from 'utils/types';
import {addResultCases, removeUndefined, round} from 'utils/helpers';

export interface IState {
  storeVariableFilters: ListResult<any>;
  stateFilters: ListResult<any>;
  dmaFilters: ListResult<any>;
  modifyInProgress: boolean;
  testControlOptionsLoading: boolean;
  testControlOptions: CampaignTestControlSelection[];
  storesResult: SingleResult<{
    count: number;
    results: any[];
    avg_sales_per_store_per_week?: number;
    avg_hero_sales_per_store_per_week?: number;
    avg_halo_sales_per_store_per_week?: number;
  }>;
  groupTotals: SingleResult<Record<string, number>>;
}

export const storeEndpoints = {
  getStores: createAsyncThunk('store/getStores', async (params: any) => {
    removeUndefined(params);
    const response = await ApiService.get(`/campaign_stores/?${new URLSearchParams(params)}`);
    return {as: 'success', value: response.data};
  }),
  deleteItem: createAsyncThunk('store/deleteItems', async (id: number) => {
    const response = await ApiService.delete(`/campaign_stores/${id}`);
    return response.data;
  }),
  getGroupTotals: createAsyncThunk('store/getGroupTotals', async (params: {campaign_id: number | string}) => {
    const response = await ApiService.get(
      `/campaign_stores/group_totals/?${new URLSearchParams({campaign_id: String(params.campaign_id)})}`,
    );
    return {value: response.data as Record<string, number>};
  }),
  getCampaignStoreAttributes: createAsyncThunk('store/getCampaignStoreAttributes', async (params: any) => {
    const response = await ApiService.get(`/campaign_store_variables/?${new URLSearchParams(params)}`);
    return response.data;
  }),
  updateStoreBulk: createAsyncThunk('store/updateStoreBulk', async (payload: any) => {
    const response = await ApiService.post('/bulk_store_update/', payload);
    return response.data;
  }),
  getCampaignStoreVariableFilters: createAsyncThunk('store/getCampaignStoreVariableFilters', async (params: any) => {
    const response = await ApiService.get(`/campaign_store_variable_filters/?${new URLSearchParams(params)}`);
    return {as: 'success', results: response.data};
  }),
  getStates: createAsyncThunk('store/getStates', async (params: any) => {
    const response = await ApiService.get(`/state/?${new URLSearchParams(params)}`);
    return {as: 'success', results: response.data};
  }),
  getDmas: createAsyncThunk('store/getDma', async (params: any) => {
    const response = await ApiService.get(`/dma/?${new URLSearchParams(params)}`);
    return {as: 'success', results: response.data};
  }),
  updateTestControl: createAsyncThunk('store/updateTestControl', async (payload: any, {rejectWithValue}) => {
    try {
      const response = await ApiService.post('/select_test_control/', payload);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  }),
  getTestControlOptions: createAsyncThunk('store/getTestControlOptions', async (params: any) => {
    const response = await ApiService.get(`/test_control_selections/?${new URLSearchParams(params)}`);
    return response.data;
  }),
  getTestControlOption: createAsyncThunk('store/getTestControlOption', async (params: any) => {
    const {id, ...rest} = params;
    const response = await ApiService.get(`/test_control_selections/${id}/?${new URLSearchParams(rest)}`);
    return response.data;
  }),
  applyTestControlOption: createAsyncThunk(
    'store/applyTestControlOption',
    async (test_control_selection_id: number) => {
      const response = await ApiService.put(`/test_control_selections/${test_control_selection_id}/`);
      return response.data;
    },
  ),
  generateTestControlPdf: createAsyncThunk('store/generateTestControlPdf', async (payload: any, {rejectWithValue}) => {
    try {
      const response = await ApiService.post('/generate_test_control_pdf/', payload, {responseType: 'blob'});
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  }),
};

export const storeSlice = createSlice({
  name: 'store',
  initialState: {
    modifyInProgress: false,
    testControlOptionsLoading: false,
    testControlOptions: [],
    storesResult: {as: 'idle'},
    groupTotals: {as: 'idle'},
    storeVariableFilters: {as: 'idle'},
    stateFilters: {as: 'idle'},
    dmaFilters: {as: 'idle'},
  } as IState,
  reducers: {
    clearTestControlOptions: state => {
      state.testControlOptions = [];
    },
  },
  extraReducers: builder => {
    addResultCases(builder, storeEndpoints.getStores, 'storesResult');
    addResultCases(builder, storeEndpoints.getGroupTotals, 'groupTotals');
    addResultCases(builder, storeEndpoints.getStates, 'stateFilters');
    addResultCases(builder, storeEndpoints.getDmas, 'dmaFilters');
    addResultCases(builder, storeEndpoints.getCampaignStoreVariableFilters, 'storeVariableFilters');
    builder
      .addCase(storeEndpoints.updateStoreBulk.pending, state => {
        state.modifyInProgress = true;
      })
      .addCase(storeEndpoints.updateStoreBulk.fulfilled, state => {
        state.modifyInProgress = false;
      })
      .addCase(storeEndpoints.updateStoreBulk.rejected, state => {
        state.modifyInProgress = false;
      })
      .addCase(storeEndpoints.getTestControlOptions.pending, state => {
        state.testControlOptionsLoading = true;
        state.testControlOptions = [];
      })
      .addCase(storeEndpoints.getTestControlOptions.fulfilled, (state, {payload}) => {
        state.testControlOptionsLoading = false;
        state.testControlOptions = payload.results.map(it => ({
          ...it,
          average_percent_difference: round(it.average_percent_difference, 2),
        }));
      })
      .addCase(storeEndpoints.getTestControlOption.pending, state => {
        state.testControlOptionsLoading = true;
        state.testControlOptions = [];
      })
      .addCase(storeEndpoints.getTestControlOption.fulfilled, (state, {payload}) => {
        state.testControlOptionsLoading = false;
        if (!payload) {
          return;
        }
        state.testControlOptions = [
          {
            ...payload,
            average_percent_difference: round(payload.average_percent_difference, 2),
          },
        ];
      });
  },
});

export const storeActions = storeSlice.actions;

export type StoreSlice = {
  [storeSlice.name]: ReturnType<(typeof storeSlice)['reducer']>;
};
